import React, { useState, useRef, useEffect } from 'react';
import {
  Box, Typography, Paper, TextField, Button, Radio, RadioGroup,
  FormControlLabel, Select, MenuItem, Dialog, DialogTitle, DialogContent,
  DialogActions, Snackbar, Alert, CircularProgress
} from '@mui/material';
import { CalendarMonth, Room } from '@mui/icons-material';
import { MapContainer, TileLayer, Marker, Circle, useMap } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';
import wikiButtonIcon from '../../assets/wiki-button.png';
import { getCountries } from '../../services/api';
import { useAuth } from '../../context/AuthContext';

// audit-backend's dedicated, unrestricted route for this tab (see /send-report-unftp)
const SEND_REPORT_URL = 'https://api.8664.wiki/api/email/send-report-unftp';

// Default Leaflet marker icons reference image files that don't survive bundling —
// point them at the CDN copies instead, otherwise the marker renders as a broken image.
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png',
  iconUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png',
  shadowUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png'
});

const DEFAULT_CENTER = [1.3521, 103.8198]; // Singapore, as a reasonable default

// Leaflet's <Circle> radius is always true ground meters, so at a fixed zoom a
// real 50km-radius circle overflows a compact map box entirely while a 5km one
// looks like a speck. Zoom/pan the map to actually fit the circle instead of
// relying on one fixed zoom level for every radius.
const FitToRadius = ({ center, radiusMeters }) => {
  const map = useMap();
  useEffect(() => {
    // LatLng.toBounds() computes the box via geodesic math directly, unlike
    // L.circle(...).getBounds() which needs the circle attached to a live map
    // (it reads the map's projection internally) - this works standalone.
    const bounds = L.latLng(center).toBounds(radiusMeters * 2);
    map.fitBounds(bounds, { padding: [20, 20] });
  }, [center, radiusMeters, map]);
  return null;
};

// yyyy-mm-dd, what a date <TextField> needs as its value regardless of display locale
const toInputDate = (date) => date.toISOString().slice(0, 10);

const WikiWasteSearch = () => {
  const { user } = useAuth();
  const [fromDate, setFromDate] = useState(() => {
    const d = new Date();
    d.setDate(d.getDate() - 7);
    return toInputDate(d);
  });
  const [toDate, setToDate] = useState(() => toInputDate(new Date()));
  const [where, setWhere] = useState('country');
  const [countryId, setCountryId] = useState('');
  const [radius, setRadius] = useState('50');
  const [position, setPosition] = useState(DEFAULT_CENTER);
  const [locating, setLocating] = useState(true);

  const [countries, setCountries] = useState([]);

  const [showDisclaimer, setShowDisclaimer] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');
  const [sending, setSending] = useState(false);

  const markerRef = useRef(null);

  useEffect(() => {
    getCountries()
      .then((res) => { if (res.success) setCountries(res.data); })
      .catch((err) => setError(err.response?.data?.error || 'Failed to load countries'));
  }, []);

  // Center the map on the user's actual current location, falling back to the
  // default if permission is denied or geolocation isn't available. The map
  // is only mounted once this resolves (see `locating` below), since Leaflet
  // only takes `center` as an initial value — it doesn't recenter on its own
  // if the prop changes after mount.
  useEffect(() => {
    if (!navigator.geolocation) {
      setLocating(false);
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setPosition([pos.coords.latitude, pos.coords.longitude]);
        setLocating(false);
      },
      () => setLocating(false),
      { enableHighAccuracy: true, timeout: 8000 }
    );
  }, []);

  const handleMarkerDragEnd = () => {
    const marker = markerRef.current;
    if (marker) {
      const latLng = marker.getLatLng();
      setPosition([latLng.lat, latLng.lng]);
    }
  };

  const handleSend = () => {
    if (!fromDate || !toDate) {
      setError('Please select both start and end dates');
      return;
    }
    if (where === 'country' && !countryId) {
      setError('Please select a country');
      return;
    }
    setShowDisclaimer(true);
  };

  const handleConfirmDisclaimer = async () => {
    setShowDisclaimer(false);
    setSending(true);

    const emailTo = user?.reportEmail || user?.email;
    let requestData = {
      startDate: fromDate,
      endDate: toDate,
      emailTo,
      emailSubject: 'WikiWASTE Pollution Report',
      includeCSV: true
    };

    if (where === 'country') {
      const country = countries.find((c) => c.id === countryId);
      requestData.country = country?.name;
      requestData.emailSubject = `WikiWASTE Pollution Report - ${country?.name}`;
    } else {
      requestData.latitude = position[0];
      requestData.longitude = position[1];
      requestData.radius = parseFloat(radius);
      requestData.emailSubject = `WikiWASTE Pollution Report - ${position[0].toFixed(4)}, ${position[1].toFixed(4)} (${radius}km)`;
    }

    try {
      const response = await fetch(SEND_REPORT_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(requestData)
      });
      const data = await response.json();
      if (!response.ok || !data.success) {
        throw new Error(data.error || data.details || 'Failed to send report');
      }
      setSuccess(true);
    } catch (err) {
      setError(err.message || 'Error sending request. Please try again.');
    } finally {
      setSending(false);
    }
  };

  return (
    <Box>
      <Typography variant="h4" gutterBottom fontWeight={600}>
        WikiWaste Search
      </Typography>

      <Paper sx={{ p: 4, maxWidth: 640 }}>
        {/* When */}
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 3, mb: 4 }}>
          <Box sx={{ textAlign: 'center', bgcolor: '#f8f9fa', borderRadius: 3, px: 2, py: 1.5, flexShrink: 0 }}>
            <CalendarMonth color="primary" />
            <Typography variant="caption" display="block" color="text.secondary" fontWeight={600}>When</Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 2, flex: 1 }}>
            <TextField
              label="From"
              type="date"
              size="small"
              fullWidth
              value={fromDate}
              onChange={(e) => setFromDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              label="To"
              type="date"
              size="small"
              fullWidth
              value={toDate}
              onChange={(e) => setToDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
          </Box>
        </Box>

        {/* Where */}
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 3 }}>
          <Box sx={{ textAlign: 'center', bgcolor: '#f8f9fa', borderRadius: 3, px: 2, py: 1.5, flexShrink: 0 }}>
            <Room color="primary" />
            <Typography variant="caption" display="block" color="text.secondary" fontWeight={600}>Where</Typography>
          </Box>

          <Box sx={{ flex: 1 }}>
            <RadioGroup value={where} onChange={(e) => setWhere(e.target.value)}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                <FormControlLabel value="country" control={<Radio size="small" />} label="Country" sx={{ flexShrink: 0, minWidth: 110 }} />
                <Select
                  size="small"
                  fullWidth
                  displayEmpty
                  value={countryId}
                  onChange={(e) => { setCountryId(e.target.value); setWhere('country'); }}
                  renderValue={(selected) => {
                    if (!selected) return <Typography color="text.secondary">Select Country</Typography>;
                    const country = countries.find((c) => c.id === selected);
                    if (!country) return '';
                    return (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        {country.flag_url && (
                          <Box component="img" src={country.flag_url} alt="" sx={{ width: 22, height: 22, borderRadius: '50%', objectFit: 'cover', flexShrink: 0 }} />
                        )}
                        <Typography component="span">{country.name}</Typography>
                      </Box>
                    );
                  }}
                >
                  <MenuItem value="">
                    <Typography color="text.secondary">Select Country</Typography>
                  </MenuItem>
                  {countries.map((c) => (
                    <MenuItem key={c.id} value={c.id} sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 1 }}>
                      {c.flag_url && (
                        <Box component="img" src={c.flag_url} alt="" sx={{ width: 24, height: 24, borderRadius: '50%', objectFit: 'cover', flexShrink: 0 }} />
                      )}
                      <Typography component="span">{c.name}</Typography>
                    </MenuItem>
                  ))}
                </Select>
              </Box>
              <FormControlLabel value="coords" control={<Radio size="small" />} label="Location — Drop a pin on map" />
            </RadioGroup>

            <Box sx={{ position: 'relative', mt: 2, height: 320, borderRadius: 2, overflow: 'hidden', boxShadow: '0 4px 20px rgba(0,0,0,0.1)' }}>
              {locating ? (
                <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 1, bgcolor: '#f8f9fa' }}>
                  <CircularProgress size={28} />
                  <Typography variant="caption" color="text.secondary">Finding your location…</Typography>
                </Box>
              ) : (
                <MapContainer center={position} zoom={11} style={{ height: '100%', width: '100%' }}>
                  {/* Esri's free World Street Map (legacy ArcGIS Online service,
                      no API key) instead of plain OSM tiles - its basemap data
                      uses English place names globally, which matters here
                      since groups span every country. Confirmed working with a
                      live curl test (200, real tile) - CartoDB Voyager and
                      Wikimedia's osm-intl were both tried first and ruled out:
                      CARTO now watermarks tiles "API KEY REQUIRED" without an
                      account, and Wikimedia returns 403 from this server. */}
                  <TileLayer
                    attribution='Esri'
                    url="https://server.arcgisonline.com/ArcGIS/rest/services/World_Street_Map/MapServer/tile/{z}/{y}/{x}"
                  />
                  <Marker
                    position={position}
                    draggable
                    eventHandlers={{ dragend: handleMarkerDragEnd }}
                    ref={markerRef}
                  />
                  <Circle
                    center={position}
                    radius={parseFloat(radius) * 1000}
                    pathOptions={{ color: '#ff0000', fillColor: '#ff0000', fillOpacity: 0.08, weight: 3 }}
                  />
                  <FitToRadius center={position} radiusMeters={parseFloat(radius) * 1000} />
                </MapContainer>
              )}
              {where !== 'coords' && (
                <Box
                  onClick={() => setWhere('coords')}
                  sx={{
                    position: 'absolute',
                    inset: 0,
                    bgcolor: 'rgba(255,255,255,0.6)',
                    cursor: 'pointer',
                    zIndex: 1000
                  }}
                />
              )}
            </Box>

            {where === 'coords' && (
              <>
                <Box sx={{ display: 'flex', justifyContent: 'center', gap: 4, mt: 2, bgcolor: '#f8f9fa', borderRadius: 2, py: 1 }}>
                  <FormControlLabel
                    control={<Radio size="small" checked={radius === '50'} onChange={() => setRadius('50')} />}
                    label="50km"
                  />
                  <FormControlLabel
                    control={<Radio size="small" checked={radius === '5'} onChange={() => setRadius('5')} />}
                    label="5km"
                  />
                </Box>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  {position[0].toFixed(5)}, {position[1].toFixed(5)}
                </Typography>
              </>
            )}
          </Box>
        </Box>

        {/* Send */}
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 4 }}>
          <Box
            component="button"
            type="button"
            onClick={handleSend}
            sx={{ border: 0, p: 0, bgcolor: 'transparent', cursor: 'pointer' }}
          >
            <Box component="img" src={wikiButtonIcon} alt="Send" sx={{ width: 75, height: 75 }} />
          </Box>
        </Box>
      </Paper>

      {/* Legal Disclaimer */}
      <Dialog open={showDisclaimer} onClose={() => setShowDisclaimer(false)} maxWidth="sm" fullWidth>
        <DialogTitle fontWeight={700} color="primary">
          WIKIWASTE – DISCLAIMER OF WARRANTIES AND LIMITATION OF LIABILITY
        </DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" fontWeight={600} sx={{ mb: 2 }}>
            Wikiwaste is a collaborative, user-generated database comprised of voluntary contributions
            from individuals and groups seeking to develop a shared resource concerning plastic pollution.
          </Typography>
          <Typography variant="body2" sx={{ mb: 2 }}>
            The information provided on this platform is for general informational purposes only. While
            we strive to keep the information up to date and correct, we make no representations or
            warranties of any kind, express or implied, about the completeness, accuracy, reliability,
            suitability or availability with respect to the website or the information, products,
            services, or related graphics contained on the website for any purpose.
          </Typography>
          <Typography variant="body2" sx={{ mb: 2 }}>
            In no event will we be liable for any loss or damage including without limitation, indirect
            or consequential loss or damage, or any loss or damage whatsoever arising from loss of data
            or profits arising out of, or in connection with, the use of this website.
          </Typography>
          <Typography variant="body2" fontWeight={600}>
            By using Wikiwaste, you acknowledge and agree to this disclaimer.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'center', pb: 3 }}>
          <Button variant="contained" size="large" sx={{ px: 5 }} onClick={handleConfirmDisclaimer} disabled={sending}>
            I Understand
          </Button>
        </DialogActions>
      </Dialog>

      {/* Sending */}
      <Dialog open={sending} maxWidth="xs" fullWidth>
        <DialogContent sx={{ textAlign: 'center', py: 4 }}>
          <CircularProgress size={40} sx={{ mb: 2 }} />
          <Typography variant="body2">Sending your report…</Typography>
        </DialogContent>
      </Dialog>

      {/* Success */}
      <Dialog open={success} onClose={() => setSuccess(false)} maxWidth="xs" fullWidth>
        <DialogContent sx={{ textAlign: 'center', py: 4 }}>
          <Typography variant="body2" sx={{ mb: 1 }}>
            Search results have been sent to your email address.
          </Typography>
          <Typography variant="caption" color="text.secondary">(Check your spam folder)</Typography>
          <Typography variant="body2" sx={{ mt: 2 }}>Thank you for contributing to Wikiwaste.</Typography>
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'center', pb: 3 }}>
          <Button variant="outlined" onClick={() => setSuccess(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={!!error} autoHideDuration={5000} onClose={() => setError('')}>
        <Alert severity="error" onClose={() => setError('')} sx={{ width: '100%' }}>{error}</Alert>
      </Snackbar>
    </Box>
  );
};

export default WikiWasteSearch;
